"use client";

import React, { useState, useEffect } from "react";
// Assuming ProductDetails is defined elsewhere, or define a basic type here

interface FocalPoint {
  position: { x: number; y: number }; // x and y in percentage
  productIds: string[]; // list of product handles/IDs
  label: string;
}

export interface FocalPointData {
  image: { src: string };
  focalPoints: FocalPoint[];
}

import { cloneNode } from "../ui-script/utils";

// Real renderCloneNode function using cloneNode utility
const renderCloneNode = async (product: ProductDetails) => {
  // Try to get the template node from window.helium.datastore.memory.html.product
  const templateNode = (window as any)?.helium?.datastore?.memory?.html
    ?.product;
  // For "existingProduct", use the product itself as a placeholder (since we have only mock data)
  const existingProduct = product;

  if (!templateNode) return null;

  // The eventName and eventData can be customized as needed
  const node = cloneNode(templateNode, product, existingProduct, false, {
    componentType: "focal_point_product_list",
    eventData: {},
  });

  // Add classes as in the reference
  node.className += " !hp-w-[200px] md:hp-border";
  if ((window as any)?.helium?.config?.classesToRemove) {
    node.classList.remove(...(window as any).helium.config.classesToRemove);
  }

  return { __html: node.outerHTML };
};

const FocalPointProductList: React.FC = ({
  focalData,
}: {
  focalData: FocalPointData;
}) => {
  const [selectedFocalPointIndex, setSelectedFocalPointIndex] = useState<
    number | null
  >(null);
  const [productList, setProductList] = useState<ProductDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showProductList, setShowProductList] = useState(false);
  const [uiProducts, setUiProducts] = useState<string[]>([]);

  // Function to handle focal point clicks and fetch product data
  const handleFocalPointClick = async (index: number) => {
    setIsLoading(true);
    setSelectedFocalPointIndex(index);
    setShowProductList(true);

    const idsToFetch = focalData.focalPoints[index].productIds;

    if (idsToFetch.length > 0) {
      try {
        // Assuming window.helium.datastore.getProduct exists and works as described
        // This part needs the actual SDK to be available in the window object
        const productPromises = idsToFetch.map((id) =>
          (window as any).helium.datastore
            .getProduct({ id })
            .catch((error: unknown) => {
              console.error(`Error fetching product ${id}:`, error);
              return null; // Return null for failed fetches
            }),
        );

        const productsResults = await Promise.all(productPromises);

        // Filter out null results and extract details
        const fetchedProducts = productsResults
          .map((result) => result?.details)
          .filter(Boolean) as ProductDetails[];

        setProductList(fetchedProducts);
      } catch (error) {
        console.error("Error fetching product details:", error);
        setProductList([]);
      } finally {
        setIsLoading(false);
      }
    } else {
      setProductList([]);
      setIsLoading(false);
    }
  };

  // Effect to clear product list when no focal point is selected
  useEffect(() => {
    if (selectedFocalPointIndex === null) {
      setProductList([]);
      setShowProductList(false);
    }
  }, [selectedFocalPointIndex]);

  // Effect to render product HTML using renderCloneNode when productList changes
  useEffect(() => {
    const fetchUiProducts = async () => {
      if (productList.length === 0) {
        setUiProducts([]);
        return;
      }
      const htmls = await Promise.all(
        productList.map((item) => renderCloneNode(item)),
      );
      setUiProducts(htmls.map((html) => html.__html));
    };
    fetchUiProducts();
  }, [productList]);
  const currentLabel =
    selectedFocalPointIndex !== null &&
    focalData.focalPoints[selectedFocalPointIndex] &&
    focalData.focalPoints[selectedFocalPointIndex].label
      ? focalData.focalPoints[selectedFocalPointIndex].label
      : null;

  return (
    <div
      className="focal-point-product-list-container hp-mx-6 md:hp-mx-20 hp-flex hp-flex-col md:hp-flex-row hp-items-center hp-justify-center"
      style={{
        position: "relative",
        backgroundColor: "white",
      }}
    >
      <div
        className="md:hp-w-1/2 hp-w-full"
        style={{
          position: "relative",
          height: "fit-content",
          maxHeight: "350px",
        }}
      >
        {/* Image */}
        <img
          src={focalData.image.src}
          alt="Product Image with Focal Points"
          style={{
            width: "auto",
            height: "100%",
            maxHeight: "350px",
            margin: "auto",
          }}
        />

        {/* Focal Points */}
        {focalData.focalPoints.map((focalPoint, index) => {
          let label = focalPoint.label.split(" ").at(-1) as string;
          if (label.endsWith("s")) label = label.slice(0, -1);
          return (
            <div
              key={index}
              className={`focal-point ${selectedFocalPointIndex === index ? "active" : ""}`}
              style={{
                position: "absolute",
                top: `${focalPoint.position.y}%`,
                left: `${focalPoint.position.x}%`,
                width: "12px", // Example size
                height: "12px", // Example size
                backgroundColor: "white",
                borderRadius: "50%",
                cursor: "pointer",
                transform: "translate(-50%, -50%)", // Center the point
                zIndex: 10, // Ensure points are above the image
                border:
                  selectedFocalPointIndex === index
                    ? "2px solid var(--brand-color-primary)"
                    : "2px solid white",
              }}
              onClick={() => handleFocalPointClick(index)}
              title={`View products for this point`}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: "-100%",
                    left: "150%",
                    fontSize: "10px",
                    color: "black",
                    //backgroundColor: "",
                    fontWeight: 700,
                    textAlign: "center",
                    width: "fit-content",
                    height: "fit-content",
                    background: "white",
                    borderRadius: "20%",
                    paddingLeft: "3px",
                    paddingRight: "3px",
                  }}
                >
                  {label}
                </span>
              </div>

              {/* Optional: Add a number or icon inside the focal point */}
            </div>
          );
        })}
      </div>

      {/* Product List */}
      {showProductList && (
        <div
          className="product-list-container md:hp-w-1/2 hp-w-full"
          style={{ marginTop: "4px" }}
        >
          {isLoading && <p>Loading products...</p>}
          {!isLoading && uiProducts.length > 0 && (
            <div>
              {currentLabel && (
                <h4
                  style={{
                    fontSize: "16px",
                    marginBottom: "12px",
                    fontWeight: 700,
                  }}
                >
                  {currentLabel}
                </h4>
              )}

              <ul
                className={`swimlane2 no-scrollbar hp-gap-[var(--h-gap,16px)] hp-pb-2 hp-pl-0 ${window.helium.productListClasses}`}
                style={{
                  marginTop: 0,
                  marginBottom: 0,
                  listStyle: "none",
                }}
              >
                {uiProducts.map((html, i) => (
                  <li
                    key={i}
                    dangerouslySetInnerHTML={{ __html: html }}
                    className="hp-shadow-md hp-rounded-[10px]"
                  />
                ))}
              </ul>
            </div>
          )}
          {!isLoading &&
            selectedFocalPointIndex !== null &&
            uiProducts.length === 0 && (
              <p>No products found for this focal point.</p>
            )}
          {!isLoading && selectedFocalPointIndex === null && (
            <p>Click on a focal point to see related products.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default FocalPointProductList;
